import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Link2, ArrowUpRight, TrendingUp, Wallet, PiggyBank, Percent } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Reveal";

const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

const Slider = ({ label, value, min, max, step, onChange, format, testId }) => (
    <div>
        <div className="flex items-baseline justify-between mb-3">
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</label>
            <span data-testid={`${testId}-value`} className="font-serif text-xl text-gold">{format(value)}</span>
        </div>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            data-testid={testId}
            className="w-full h-1 appearance-none bg-white/10 accent-[#EAB308] cursor-pointer"
        />
        <div className="flex justify-between text-[10px] text-slate-600 mt-1.5">
            <span>{format(min)}</span>
            <span>{format(max)}</span>
        </div>
    </div>
);

const ResultCard = ({ icon: Icon, label, value, accent, testId }) => (
    <div data-testid={testId} className={`border p-5 ${accent ? "border-gold/50 bg-gold/[0.05]" : "border-white/10 bg-paper"}`}>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
            <Icon size={13} className={accent ? "text-gold" : "text-slate-500"} /> {label}
        </div>
        <motion.p
            key={value}
            initial={{ opacity: 0.4, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className={`font-serif text-2xl md:text-3xl tracking-tight ${accent ? "text-gold" : "text-white"}`}
        >
            {value}
        </motion.p>
    </div>
);

function compoundSeries(initial, monthly, annualRate, years) {
    const i = Math.pow(1 + annualRate / 100, 1 / 12) - 1;
    const series = [];
    let balance = initial;
    let invested = initial;
    for (let y = 0; y <= years; y++) {
        series.push({ year: y, balance, invested });
        for (let m = 0; m < 12; m++) {
            balance = balance * (1 + i) + monthly;
            invested += monthly;
        }
    }
    return series;
}

const GrowthChart = ({ series }) => {
    const W = 560, H = 220, PAD = 8;
    const maxV = Math.max(...series.map((s) => s.balance), 1);
    const x = (idx) => PAD + (idx * (W - 2 * PAD)) / (series.length - 1);
    const y = (v) => H - PAD - (v / maxV) * (H - 2 * PAD);

    const balPath = series.map((s, idx) => `${idx === 0 ? "M" : "L"}${x(idx).toFixed(1)},${y(s.balance).toFixed(1)}`).join(" ");
    const invPath = series.map((s, idx) => `${idx === 0 ? "M" : "L"}${x(idx).toFixed(1)},${y(s.invested).toFixed(1)}`).join(" ");
    const areaPath = `${balPath} L${x(series.length - 1)},${H - PAD} L${x(0)},${H - PAD} Z`;

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-48 md:h-56" aria-hidden="true">
            <defs>
                <linearGradient id="calc-fade" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EAB308" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#EAB308" stopOpacity="0" />
                </linearGradient>
            </defs>
            {[0.25, 0.5, 0.75].map((f) => (
                <line key={f} x1={0} y1={H * f} x2={W} y2={H * f} stroke="rgba(255,255,255,0.06)" />
            ))}
            <path d={areaPath} fill="url(#calc-fade)" />
            <path d={invPath} fill="none" stroke="#64748B" strokeWidth="1.6" strokeDasharray="5 5" />
            <path d={balPath} fill="none" stroke="#EAB308" strokeWidth="2.4" strokeLinejoin="round" />
            <circle cx={x(series.length - 1)} cy={y(series[series.length - 1].balance)} r="4.5" fill="#EAB308" />
        </svg>
    );
};

const CompoundCalculator = () => {
    const [initial, setInitial] = useState(1000);
    const [monthly, setMonthly] = useState(500);
    const [rate, setRate] = useState(10);
    const [years, setYears] = useState(20);

    const series = useMemo(() => compoundSeries(initial, monthly, rate, years), [initial, monthly, rate, years]);
    const last = series[series.length - 1];
    const interest = last.balance - last.invested;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div className="flex flex-col gap-8">
                <Slider label="Aporte inicial" value={initial} min={0} max={100000} step={500} onChange={setInitial} format={brl} testId="calc-initial-slider" />
                <Slider label="Aporte mensal" value={monthly} min={0} max={10000} step={100} onChange={setMonthly} format={brl} testId="calc-monthly-slider" />
                <Slider label="Taxa anual estimada" value={rate} min={1} max={20} step={0.5} onChange={setRate} format={(v) => `${v}% a.a.`} testId="calc-rate-slider" />
                <Slider label="Período" value={years} min={1} max={40} step={1} onChange={setYears} format={(v) => `${v} anos`} testId="calc-years-slider" />
            </div>
            <div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <ResultCard icon={PiggyBank} label="Patrimônio final" value={brl(last.balance)} accent testId="calc-result-final" />
                    <ResultCard icon={Wallet} label="Total investido" value={brl(last.invested)} testId="calc-result-invested" />
                    <ResultCard icon={Percent} label="Juros acumulados" value={brl(interest)} testId="calc-result-interest" />
                </div>
                <div className="border border-white/10 bg-paper p-4">
                    <GrowthChart series={series} />
                    <div className="flex items-center gap-6 px-2 pb-1 text-[11px] text-slate-500">
                        <span className="flex items-center gap-2"><span className="w-4 h-0.5 bg-gold" /> Patrimônio</span>
                        <span className="flex items-center gap-2"><span className="w-4 h-0.5 bg-slate-500" style={{ backgroundImage: "repeating-linear-gradient(90deg,#64748B 0 4px,transparent 4px 8px)" }} /> Total investido</span>
                    </div>
                </div>
                <p className="mt-4 text-xs text-slate-600 leading-relaxed">
                    Simulação com capitalização mensal, sem descontar impostos ou inflação. Rentabilidade passada não garante resultados futuros.
                </p>
            </div>
        </div>
    );
};

const FourPercentCalculator = () => {
    const [expense, setExpense] = useState(5000);

    const rows = [
        { rate: 3, mult: 400 },
        { rate: 3.5, mult: 343 },
        { rate: 4, mult: 300 },
    ];
    const target = expense * 12 * 25;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div>
                <Slider label="Gasto mensal desejado na aposentadoria" value={expense} min={1000} max={30000} step={250} onChange={setExpense} format={brl} testId="calc4-expense-slider" />
                <div className="mt-8 border border-white/10 bg-paper p-5 text-sm text-slate-400 leading-relaxed">
                    A regra dos 4% diz que você pode retirar 4% do patrimônio por ano, corrigidos pela inflação,
                    com baixa probabilidade de o dinheiro acabar em 30 anos. Por isso o patrimônio-alvo é
                    <span className="text-gold"> 25× o seu gasto anual</span> — ou 300× o gasto mensal.
                </div>
            </div>
            <div>
                <ResultCard icon={TrendingUp} label="Patrimônio necessário (regra dos 4%)" value={brl(target)} accent testId="calc4-result-target" />
                <div className="mt-6 border border-white/10">
                    <div className="grid grid-cols-3 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        <span>Taxa segura</span>
                        <span className="text-right">Multiplicador</span>
                        <span className="text-right">Patrimônio-alvo</span>
                    </div>
                    {rows.map((r) => (
                        <div key={r.rate} data-testid={`calc4-row-${r.rate}`} className={`grid grid-cols-3 px-5 py-4 border-b border-white/5 text-sm ${r.rate === 4 ? "bg-gold/[0.05]" : ""}`}>
                            <span className={r.rate === 4 ? "text-gold font-medium" : "text-slate-300"}>{r.rate}% ao ano</span>
                            <span className="text-right text-slate-400">×{r.mult}</span>
                            <span className="text-right font-serif text-lg">{brl(expense * 12 * (r.mult / 12))}</span>
                        </div>
                    ))}
                </div>
                <p className="mt-4 text-xs text-slate-600 leading-relaxed">
                    No Brasil, planejadores costumam usar 3% a 3,5% por prudência, mesmo com juros reais historicamente altos.
                </p>
            </div>
        </div>
    );
};

export default function Calculadora() {
    const copyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success("Link copiado. Compartilhe com quem precisa ver isso.");
    };

    return (
        <main data-testid="calculadora-page" className="pt-28 md:pt-36 pb-20">
            <div className="max-w-3xl mx-auto px-4 md:px-0 text-center md:text-left">
                <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">Ferramenta gratuita</span>
                <h1 className="font-serif text-4xl md:text-6xl tracking-tight mt-4 leading-[1.05]">
                    <MaskedLine delay={0.1}>Veja o tempo</MaskedLine>
                    <MaskedLine delay={0.25}>trabalhar <span className="italic text-gold">por você.</span></MaskedLine>
                </h1>
                <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-xl">
                    Duas simulações que mudam a forma como você enxerga dinheiro:
                    os juros compostos construindo patrimônio — e o número exato da sua independência financeira.
                </p>
                <button
                    data-testid="calc-share-button"
                    onClick={copyLink}
                    className="mt-6 inline-flex items-center gap-2 border border-white/15 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-slate-300 hover:border-gold hover:text-gold transition-colors"
                >
                    <Link2 size={14} /> Compartilhar esta calculadora
                </button>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 mt-16 md:mt-24">
                <Reveal>
                    <div className="flex items-baseline gap-4 mb-10">
                        <span className="font-serif text-5xl md:text-6xl text-white/[0.08]">01</span>
                        <div>
                            <h2 className="font-serif text-2xl md:text-4xl tracking-tight">Juros compostos</h2>
                            <p className="text-sm text-slate-500 mt-1">Quanto seus aportes mensais viram com o tempo.</p>
                        </div>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <CompoundCalculator />
                </Reveal>

                <div className="mt-12 flex justify-center md:justify-start">
                    <Link to="/artigo/juros-compostos-forca-silenciosa" data-testid="calc-article-link" className="group inline-flex items-center gap-2 text-sm text-gold hover:text-golddark transition-colors">
                        Leia o artigo: Juros compostos, a força silenciosa
                        <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </Link>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 md:px-8 mt-24 md:mt-32">
                <Reveal>
                    <div className="flex items-baseline gap-4 mb-10">
                        <span className="font-serif text-5xl md:text-6xl text-white/[0.08]">02</span>
                        <div>
                            <h2 className="font-serif text-2xl md:text-4xl tracking-tight">Regra dos 4%</h2>
                            <p className="text-sm text-slate-500 mt-1">O patrimônio que paga a sua liberdade, para sempre.</p>
                        </div>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <FourPercentCalculator />
                </Reveal>

                <div className="mt-12 flex justify-center md:justify-start">
                    <Link to="/artigo/regra-dos-4-por-cento-independencia" data-testid="calc4-article-link" className="group inline-flex items-center gap-2 text-sm text-gold hover:text-golddark transition-colors">
                        Leia o artigo: A regra dos 4% explicada sem mistério
                        <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </Link>
                </div>
            </div>
        </main>
    );
}
